import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Printer, Volume2, VolumeX, ChefHat, AlarmClock } from "lucide-react";
import { useNotificationSound, useNewItemDetector } from "@/lib/sound";

const STATUSES = [
  "draft", "submitted", "restaurant_accepted", "deposit_paid",
  "confirmed", "completed", "cancelled", "no_show", "rejected",
];

const PILL = (s) => ({
  submitted: "bg-amber-soft text-ink",
  restaurant_accepted: "bg-amber/20 text-ink",
  deposit_paid: "bg-ink text-cream",
  confirmed: "bg-ink text-cream",
  completed: "bg-green-100 text-green-900",
  cancelled: "bg-tablo-border text-light-ink",
  rejected: "bg-rose-100 text-rose-900",
  no_show: "bg-rose-200 text-rose-900",
  draft: "bg-cream text-light-ink",
})[s] || "bg-cream";

const POLL_MS = 20000;

export default function AdminBookings() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const [diningFilter, setDiningFilter] = useState("all"); // all | package | a_la_carte
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("tablo_admin_sound") === "on");
  const ding = useNotificationSound();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/bookings", {
        params: filter === "all" ? {} : { status: filter },
      });
      setList(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  }, [filter]);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  // Sound on new submitted bookings
  useNewItemDetector(
    list.filter((b) => b.status === "submitted"),
    (additions) => {
      if (!additions.length) return;
      if (soundOn) ding();
      additions.forEach((b) => {
        toast(`New booking — ${b.restaurant?.name} · ${b.pax} pax · ${b.date} ${b.time}`, {
          description: `Organizer: ${b.organizer?.name || b.organizer?.phone || "—"}`,
        });
      });
    }
  );

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("tablo_admin_sound", next ? "on" : "off");
    if (next) ding();
  };

  const override = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}/status`, { status });
      toast.success("Status updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const shown = list.filter((b) => {
    if (diningFilter === "all") return true;
    if (diningFilter === "package") return b.dining_kind === "package";
    if (diningFilter === "a_la_carte") return b.dining_kind === "a_la_carte";
    return true;
  });

  return (
    <div className="space-y-5 animate-fade-up">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-amber text-xs uppercase tracking-[0.18em] font-medium mb-2">Bookings</p>
          <h1 className="font-serif text-4xl">All bookings</h1>
          <p className="text-light-ink text-sm mt-1">Polling every 20s. Toggle sound to be alerted on new submissions.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={toggleSound} className="tablo-btn-outline" data-testid="admin-sound-toggle">
            {soundOn ? <Volume2 className="w-4 h-4 mr-1.5" strokeWidth={1.6}/> : <VolumeX className="w-4 h-4 mr-1.5" strokeWidth={1.6}/>}
            Sound {soundOn ? "on" : "off"}
          </Button>
          <div className="w-40">
            <Select value={diningFilter} onValueChange={setDiningFilter}>
              <SelectTrigger data-testid="admin-dining-filter"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All modes</SelectItem>
                <SelectItem value="package">Package</SelectItem>
                <SelectItem value="a_la_carte">À-la-carte</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-44">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="admin-filter"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      <div className="tablo-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-cream">
            <tr className="text-xs uppercase tracking-wider text-light-ink">
              <th className="text-left p-3">Date / Time</th>
              <th className="text-left p-3">Restaurant</th>
              <th className="text-left p-3">Organizer</th>
              <th className="text-left p-3">Mode</th>
              <th className="text-left p-3">Pax</th>
              <th className="text-left p-3">Deposit</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Override</th>
              <th className="text-left p-3">Print</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={9} className="p-6 text-center text-light-ink">No bookings.</td></tr>
            )}
            {shown.map((b) => (
              <tr key={b.id} className="border-t border-tablo-border hover:bg-off-white" data-testid={`admin-booking-row-${b.id}`}>
                <td className="p-3">{b.date} <span className="text-light-ink">{b.time}</span></td>
                <td className="p-3"><div className="font-medium">{b.restaurant?.name}</div><div className="text-xs text-light-ink">{b.restaurant?.area}</div></td>
                <td className="p-3"><div>{b.organizer?.name || "—"}</div><div className="text-xs text-light-ink">{b.organizer?.email || b.organizer?.phone}</div></td>
                <td className="p-3">
                  {b.dining_kind === "package" ? (
                    <span className="inline-flex items-center gap-1 text-xs"><ChefHat className="w-3 h-3" strokeWidth={1.6}/> Package</span>
                  ) : (
                    <span className="text-xs text-light-ink">À-la-carte</span>
                  )}
                  {b.dining_kind === "package" && b.acceptance_deadline_at && (
                    <div className="text-[10px] text-light-ink mt-0.5 inline-flex items-center gap-1">
                      <AlarmClock className="w-3 h-3" strokeWidth={1.6}/> by {new Date(b.acceptance_deadline_at).toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}
                    </div>
                  )}
                </td>
                <td className="p-3">{b.pax}</td>
                <td className="p-3">₹{(b.deposit_amount_inr || 0).toLocaleString("en-IN")}</td>
                <td className="p-3"><span className={`tablo-pill ${PILL(b.status)}`}>{b.status.replace("_", " ")}</span></td>
                <td className="p-3">
                  <Select value={b.status} onValueChange={(v) => override(b.id, v)}>
                    <SelectTrigger className="w-40 h-8 text-xs" data-testid={`admin-override-${b.id}`}><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s} className="text-xs">{s.replace("_", " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-3">
                  <Link
                    to={`/print/${b.id}`}
                    target="_blank"
                    className="tablo-btn-outline text-xs inline-flex items-center"
                    data-testid={`admin-print-${b.id}`}
                  >
                    <Printer className="w-3.5 h-3.5 mr-1" strokeWidth={1.6}/> Print / PDF
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
